// "Rutas" are the narrative layer of Learn: instead of one more standalone
// article, each route follows a single thread across several real countries,
// stop by stop, and links every stop to that country's page in the app. The
// flagship idea is the journey of an everyday object — the point being that
// the reason each country plays the role it plays is almost always
// historical, not natural.

export interface LearnRouteStopLocale {
  /** The country's role at this stop, e.g. "Where it comes from". */
  role: string;
  title: string;
  body: string;
}

export interface LearnRouteStop {
  /** English country name — used to deep-link and to locate it on the map. */
  country: string;
  emoji: string;
  es: LearnRouteStopLocale;
  en: LearnRouteStopLocale;
}

export interface LearnRouteLocale {
  title: string;
  subtitle: string;
  intro: string;
  closing: string;
}

export interface LearnRoute {
  slug: string;
  /**
   * The slug an English reader sees in the URL. `slug` stays the canonical
   * id — `related` arrays and every internal lookup use it — so translating
   * the URLs never moves anything that points at an item.
   */
  slugEn: string;
  emoji: string;
  /** Slugs of library articles that go deeper on this route's themes. */
  related?: string[];
  es: LearnRouteLocale;
  en: LearnRouteLocale;
  stops: LearnRouteStop[];
}

export const learnRoutes: LearnRoute[] = [
  {
    slug: "viaje-de-tus-datos",
    slugEn: "journey-of-your-data",
    emoji: "🛜",
    related: [
      "mar-zonas-economicas",
      "colonizacion-divisiones",
      "primer-tercer-mundo",
    ],
    es: {
      title: "El viaje de tus datos",
      subtitle: "Un mensaje, cinco países y un cable en el fondo del mar",
      intro:
        "Se dice que los datos están \"en la nube\". Están en el fondo del océano. Más del 95% del tráfico internacional de internet viaja por cables submarinos del grosor de una manguera de jardín, que amarran en playas concretas de países concretos. Seguir un mensaje muestra que internet no flota: tiene geografía, y esa geografía se parece mucho a la del comercio que vino antes.",
      closing:
        "El recorrido deja una conclusión incómoda: la conectividad no depende de la tecnología que tiene un país sino de por dónde pasan los cables, y por dónde pasan los cables lo deciden quienes los pagan. Durante un siglo fueron consorcios de empresas telefónicas; hoy, buena parte de la capacidad nueva la construyen y la poseen cuatro o cinco empresas de contenido. El mapa de internet se está redibujando, y esta vez no lo dibujan los Estados.",
    },
    en: {
      title: "The journey of your data",
      subtitle: "One message, five countries and a cable on the seabed",
      intro:
        "Data is said to live \"in the cloud\". It lives on the ocean floor. More than 95% of international internet traffic travels through submarine cables about as thick as a garden hose, landing on specific beaches in specific countries. Following a message shows that the internet doesn't float: it has a geography, and that geography looks a great deal like the trade that came before it.",
      closing:
        "The route leaves an uncomfortable conclusion: a country's connectivity depends less on the technology it has than on where the cables run, and where the cables run is decided by whoever pays for them. For a century that meant consortia of telephone companies; today much of the new capacity is built and owned by four or five content companies. The map of the internet is being redrawn, and this time states are not the ones drawing it.",
    },
    stops: [
      {
        country: "Brazil",
        emoji: "🔌",
        es: {
          role: "Dónde se enchufa el continente",
          title: "Fortaleza, la puerta de Sudamérica",
          body: "Casi todos los cables que conectan Sudamérica con el resto del mundo amarran en un tramo de costa del nordeste brasileño, alrededor de Fortaleza. Hasta hace poco eso tenía una consecuencia rara: un mail de Buenos Aires a Madrid viajaba primero a Estados Unidos y recién después cruzaba a Europa, porque no existía un cable directo. El primer enlace directo entre Sudamérica y Europa se activó recién en 2021. El recorrido no seguía la geografía sino la inversión, y la inversión había ido hacia el norte.",
        },
        en: {
          role: "Where the continent plugs in",
          title: "Fortaleza, South America's door",
          body: "Almost every cable connecting South America to the rest of the world lands on a stretch of north-eastern Brazilian coast around Fortaleza. Until recently that had an odd consequence: an email from Buenos Aires to Madrid travelled to the United States first and only then crossed to Europe, because no direct cable existed. The first direct South America to Europe link went live only in 2021. The path followed not geography but investment, and investment had gone north.",
        },
      },
      {
        country: "Egypt",
        emoji: "⏳",
        es: {
          role: "El cuello de botella",
          title: "Egipto, por donde pasa casi todo",
          body: "Para ir de Europa a Asia, un cable tiene que atravesar Egipto: entra por el Mediterráneo, cruza el país por tierra o por el canal y sale al Mar Rojo. Una cantidad enorme del tráfico entre los dos continentes pasa por ese corredor angosto, y Egipto cobra por el tránsito. La fragilidad quedó a la vista en 2024, cuando cortes de cables en el Mar Rojo degradaron la conexión de varios países de Asia y África a la vez. Es el mismo estrangulamiento geográfico que hizo importante al canal de Suez, aplicado a otra cosa que pasa por ahí.",
        },
        en: {
          role: "The bottleneck",
          title: "Egypt, where nearly everything passes",
          body: "To get from Europe to Asia, a cable has to cross Egypt: in from the Mediterranean, overland or along the canal, out into the Red Sea. An enormous share of traffic between the two continents runs through that narrow corridor, and Egypt charges for the transit. The fragility showed in 2024, when cable cuts in the Red Sea degraded connectivity across several Asian and African countries at once. It is the same geographic chokehold that made the Suez Canal matter, applied to something else that passes through.",
        },
      },
      {
        country: "Djibouti",
        emoji: "⚓",
        es: {
          role: "Donde la posición vale más que el tamaño",
          title: "Yibuti, 23.000 km² en el lugar exacto",
          body: "Yibuti ocupa menos que la provincia de Misiones, y es uno de los nodos de cables más importantes entre Asia, África y Europa. La razón es una sola: está sobre el estrecho de Bab el-Mandeb, la boca del Mar Rojo. Por el mismo motivo alberga bases militares de varios países a la vez, algo insólito para su tamaño. Es el ejemplo más limpio de algo que esta app suele esconder detrás del dato de superficie: en geografía, dónde estás parado puede importar mucho más que cuánto ocupás.",
        },
        en: {
          role: "Where position beats size",
          title: "Djibouti, 23,000 km² in exactly the right place",
          body: "Djibouti is smaller than many provinces, and it is one of the most important cable nodes between Asia, Africa and Europe. The reason is single: it sits on the Bab el-Mandeb strait, the mouth of the Red Sea. For the same reason it hosts military bases from several countries at once, extraordinary for its size. It is the cleanest example of something a surface-area figure tends to hide: in geography, where you stand can matter far more than how much you take up.",
        },
      },
      {
        country: "Tchad",
        emoji: "🏝️",
        es: {
          role: "El otro extremo",
          title: "Chad, un país sin costa y sin cable",
          body: "Un cable submarino necesita una playa. Los países sin salida al mar no tienen ninguna, así que tienen que comprarle el tránsito a un vecino, con lo que eso implica: más caro, más lento y dependiente de una relación diplomática. Chad tiene 12,6% de acceso a internet, contra 94,4% de Singapur. Parte de esa distancia es ingreso y parte es infraestructura interna, pero una parte es pura geografía heredada: las fronteras que dejaron los repartos coloniales decidieron qué países iban a tener costa, mucho antes de que existiera nada que enchufar.",
        },
        en: {
          role: "The other end",
          title: "Chad, a country with no coast and no cable",
          body: "A submarine cable needs a beach. Landlocked countries have none, so they have to buy transit from a neighbour, with everything that implies: more expensive, slower and dependent on a diplomatic relationship. Chad has 12.6% internet access against Singapore's 94.4%. Part of that distance is income and part is domestic infrastructure, but part is inherited geography: the borders left by colonial partition decided which countries would have a coast, long before there was anything to plug in.",
        },
      },
      {
        country: "United States",
        emoji: "🏢",
        es: {
          role: "Dónde descansa",
          title: "Estados Unidos, donde el dato se queda quieto",
          body: "Al final del viaje el dato se guarda en un centro de datos, y una porción desproporcionada de esos edificios está en Estados Unidos. Eso tiene una consecuencia que no es técnica sino legal: un archivo guardado en un país queda alcanzado por las leyes de ese país, sin importar de dónde sea quien lo subió. Y algo cambió en la última década: los cables ya no los construyen consorcios de telefónicas sino, en buena medida, las mismas empresas dueñas de los servicios que usás. Poseer el contenido, el centro de datos y el cable que los une es una concentración que la infraestructura de comunicaciones no había tenido nunca.",
        },
        en: {
          role: "Where it rests",
          title: "The United States, where the data sits still",
          body: "At the end of the journey the data is stored in a data centre, and a disproportionate share of those buildings sits in the United States. That has a consequence which is not technical but legal: a file held in one country falls under that country's law, whoever uploaded it and from wherever. And something shifted over the past decade: cables are no longer built by telephone consortia but, in large part, by the same companies that own the services you use. Owning the content, the data centre and the cable between them is a concentration communications infrastructure has never had before.",
        },
      },
    ],
  },
  {
    slug: "viaje-del-cafe",
    slugEn: "journey-of-a-coffee",
    emoji: "☕",
    related: [
      "colonizacion-divisiones",
      "esclavitud-comercio-triangular",
      "primer-tercer-mundo",
    ],
    es: {
      title: "El viaje de un café",
      subtitle: "De una montaña etíope a tu taza, pasando por cuatro países",
      intro:
        "Tomar un café es una de las cosas más cotidianas que hacemos, y también una de las que más países involucra. Seguí el recorrido del grano y vas a entender, de paso, por qué los países que producen café casi nunca son los que más dinero ganan con él.",
      closing:
        "El patrón que acabás de recorrer se repite con el cacao, el algodón, el litio o el petróleo: el país que extrae la materia prima captura una fracción mínima del valor final, y el que la procesa y le pone marca captura la mayor parte. No es una casualidad del mercado: es la estructura que dejó instalada la economía colonial, y cambiarla es el problema económico central de buena parte del mundo.",
    },
    en: {
      title: "The journey of a coffee",
      subtitle: "From an Ethiopian mountainside to your cup, via four countries",
      intro:
        "Drinking a coffee is one of the most ordinary things we do, and also one that involves the most countries. Follow the bean's route and you'll understand, along the way, why the countries that grow coffee are almost never the ones that make the most money from it.",
      closing:
        "The pattern you just followed repeats with cocoa, cotton, lithium and oil: the country that extracts the raw material captures a tiny fraction of the final value, and the one that processes and brands it captures most of it. That isn't a quirk of the market — it's the structure the colonial economy left in place, and changing it is the central economic problem for much of the world.",
    },
    stops: [
      {
        country: "Ethiopia",
        emoji: "🌱",
        es: {
          role: "Dónde nace",
          title: "Etiopía, el origen real del café",
          body: "El café arábica es originario de las tierras altas etíopes, donde todavía crece de forma silvestre. Desde ahí se difundió al mundo árabe —el nombre \"café\" llega al español vía el árabe qahwa y el turco kahve— y desde el puerto yemení de Moca al resto del planeta. Etiopía sigue siendo hoy uno de los mayores productores de África, y el café representa una parte enorme de sus exportaciones.",
        },
        en: {
          role: "Where it begins",
          title: "Ethiopia, coffee's actual origin",
          body: "Arabica coffee is native to the Ethiopian highlands, where it still grows wild. From there it spread to the Arab world — the word \"coffee\" reaches English via Arabic qahwa and Turkish kahve — and from the Yemeni port of Mocha to the rest of the planet. Ethiopia remains one of Africa's largest producers, and coffee accounts for an enormous share of its exports.",
        },
      },
      {
        country: "Brazil",
        emoji: "🌾",
        es: {
          role: "Dónde se produce a escala",
          title: "Brasil, el gigante del grano",
          body: "Brasil es, desde hace más de un siglo, el mayor productor mundial de café. Pero el cultivo no llegó ahí por la geografía: fue implantado deliberadamente en el período colonial y se expandió sobre la base del trabajo esclavizado —Brasil fue el último país de América en abolir la esclavitud, en 1888— y después con oleadas de inmigrantes europeos y japoneses. La escala brasileña es tan grande que sus cosechas mueven el precio internacional del café.",
        },
        en: {
          role: "Where it's grown at scale",
          title: "Brazil, the giant of the bean",
          body: "Brazil has been the world's largest coffee producer for over a century. But the crop didn't land there because of geography: it was deliberately planted in the colonial period and expanded on the back of enslaved labour — Brazil was the last country in the Americas to abolish slavery, in 1888 — and later through waves of European and Japanese immigrants. Brazil's scale is such that its harvests move the international price of coffee.",
        },
      },
      {
        country: "Vietnam",
        emoji: "📈",
        es: {
          role: "El competidor inesperado",
          title: "Vietnam, de la nada al segundo puesto",
          body: "Vietnam casi no producía café hasta los años 80. Tras la guerra y en el marco de las reformas económicas del Doi Moi, el Estado impulsó masivamente el cultivo de robusta, y en dos décadas el país pasó a ser el segundo productor mundial. Esa irrupción tiró los precios globales hacia abajo en los 90 y 2000, con consecuencias duras para millones de pequeños productores en África y América Latina: una muestra de cómo una decisión de política interna puede reordenar un mercado mundial.",
        },
        en: {
          role: "The unexpected competitor",
          title: "Vietnam, from nothing to second place",
          body: "Vietnam barely grew coffee until the 1980s. After the war, and as part of the Doi Moi economic reforms, the state pushed robusta cultivation hard, and within two decades the country became the world's second-largest producer. That surge drove global prices down through the 1990s and 2000s, with harsh consequences for millions of smallholders in Africa and Latin America — a demonstration of how one country's domestic policy can reorder a world market.",
        },
      },
      {
        country: "Germany",
        emoji: "🏭",
        es: {
          role: "Dónde se agrega el valor",
          title: "Alemania, un gigante cafetero sin una sola planta",
          body: "Alemania no cultiva un solo grano de café y, sin embargo, es uno de los mayores exportadores de café del mundo. La razón es que importa grano verde, lo tuesta, lo empaqueta y lo reexporta con marca y valor agregado. Ahí está la clave del reparto: el tostado, el marketing y la distribución —no el cultivo— concentran la mayor parte del precio que pagás en la caja.",
        },
        en: {
          role: "Where the value is added",
          title: "Germany, a coffee giant without a single plant",
          body: "Germany doesn't grow a single coffee bean, and yet it's one of the world's largest coffee exporters. The reason is that it imports green beans, roasts them, packages them and re-exports them with a brand and added value. That's where the split happens: roasting, marketing and distribution — not farming — capture most of the price you pay at the counter.",
        },
      },
      {
        country: "Colombia",
        emoji: "🏷️",
        es: {
          role: "La excepción que confirma la regla",
          title: "Colombia y el valor de tener marca propia",
          body: "Colombia hizo algo que la mayoría de los países productores no logró: construyó una marca país alrededor de su café. La Federación Nacional de Cafeteros y la figura de Juan Valdez convirtieron \"café colombiano\" en un sello reconocible que se vende más caro que el grano genérico. Es la prueba de que el reparto desigual del valor no es una ley natural, sino algo que se puede disputar —aunque sea difícil y lleve décadas.",
        },
        en: {
          role: "The exception that proves the rule",
          title: "Colombia and the value of owning a brand",
          body: "Colombia did something most producing countries never managed: it built a national brand around its coffee. The National Federation of Coffee Growers and the figure of Juan Valdez turned \"Colombian coffee\" into a recognisable mark that sells above the generic bean. It's proof that the uneven split of value isn't a law of nature but something that can be contested — even if it's hard and takes decades.",
        },
      },
    ],
  },
  {
    slug: "bateria-del-telefono",
    slugEn: "your-phones-battery",
    emoji: "🔋",
    related: ["cambio-climatico", "colonizacion-divisiones", "capitalismo-y-alternativas"],
    es: {
      title: "La batería de tu teléfono",
      subtitle: "Cinco países, un mineral y la disputa geopolítica del siglo",
      intro:
        "El teléfono con el que probablemente estás leyendo esto contiene materiales de al menos una decena de países. Seguir el recorrido de su batería explica buena parte de la geopolítica actual: quién tiene los minerales, quién los procesa y por qué eso no es lo mismo.",
      closing:
        "La transición energética no elimina la dependencia de recursos extraídos del suelo: la traslada del petróleo al litio, el cobalto, el níquel y las tierras raras. Eso redibuja el mapa del poder, y explica por qué países que antes no figuraban en la conversación geopolítica hoy están en el centro de ella.",
    },
    en: {
      title: "Your phone's battery",
      subtitle: "Five countries, one mineral and the geopolitical fight of the century",
      intro:
        "The phone you're probably reading this on contains materials from at least a dozen countries. Following its battery explains a good deal of today's geopolitics: who has the minerals, who processes them, and why those aren't the same thing.",
      closing:
        "The energy transition doesn't remove dependence on things dug out of the ground: it shifts it from oil to lithium, cobalt, nickel and rare earths. That redraws the map of power, and explains why countries that weren't part of the geopolitical conversation are now at the centre of it.",
    },
    stops: [
      {
        country: "Democratic Republic of the Congo",
        emoji: "⛏️",
        es: {
          role: "El cuello de botella",
          title: "RD del Congo, donde está el cobalto",
          body: "Alrededor de tres cuartas partes del cobalto del mundo sale de la República Democrática del Congo, un mineral clave para las baterías de iones de litio. Buena parte se extrae en minas industriales, pero una porción significativa proviene de minería artesanal, con condiciones laborales que organismos internacionales denuncian desde hace años, incluido el trabajo infantil. El país es, al mismo tiempo, inmensamente rico en recursos y uno de los más pobres del mundo por ingreso per cápita: la llamada \"maldición de los recursos\".",
        },
        en: {
          role: "The bottleneck",
          title: "DR Congo, where the cobalt is",
          body: "Roughly three quarters of the world's cobalt comes out of the Democratic Republic of the Congo, a mineral critical to lithium-ion batteries. Much is mined industrially, but a significant share comes from artisanal mining, under working conditions international bodies have flagged for years, child labour among them. The country is simultaneously immensely rich in resources and one of the poorest in the world by income per head — the so-called resource curse.",
        },
      },
      {
        country: "Chile",
        emoji: "🧂",
        es: {
          role: "El triángulo del litio",
          title: "Chile, Argentina y Bolivia sobre el mismo salar",
          body: "Entre los salares del norte de Chile, el noroeste argentino y el altiplano boliviano se concentra más de la mitad de las reservas mundiales de litio. Cada país eligió un camino distinto: Chile con un modelo de concesiones estatales estrictas, Argentina con un esquema más abierto a la inversión privada provincia por provincia, y Bolivia con un control estatal que hasta ahora produjo mucho menos de lo esperado. El mismo recurso, tres decisiones políticas, tres resultados.",
        },
        en: {
          role: "The lithium triangle",
          title: "Chile, Argentina and Bolivia on the same salt flats",
          body: "Between the salt flats of northern Chile, north-western Argentina and the Bolivian altiplano sits more than half the world's known lithium reserves. Each country chose a different path: Chile with tightly controlled state concessions, Argentina with a scheme more open to private investment province by province, and Bolivia with state control that has so far produced far less than expected. The same resource, three political decisions, three outcomes.",
        },
      },
      {
        country: "China",
        emoji: "⚗️",
        es: {
          role: "Donde se refina",
          title: "China, el paso que casi nadie ve",
          body: "Tener el mineral no alcanza: hay que refinarlo. Y China procesa una porción dominante del litio, el cobalto y las tierras raras del mundo, además de fabricar la mayoría de las celdas de batería. Esa concentración le da una palanca geopolítica enorme, porque un país puede tener reservas gigantes y aun así depender de otro para convertirlas en algo utilizable. Es la diferencia entre tener el recurso y controlar la cadena.",
        },
        en: {
          role: "Where it's refined",
          title: "China, the step almost nobody sees",
          body: "Having the mineral isn't enough: it has to be refined. And China processes a dominant share of the world's lithium, cobalt and rare earths, on top of manufacturing most battery cells. That concentration hands it enormous geopolitical leverage, because a country can hold vast reserves and still depend on another to turn them into something usable. That's the difference between having the resource and controlling the chain.",
        },
      },
      {
        country: "Taiwan (officially Republic of China)",
        emoji: "🧠",
        es: {
          role: "El eslabón más disputado",
          title: "Taiwán y los chips que nadie más sabe hacer",
          body: "La batería alimenta un teléfono cuyo cerebro son chips que se fabrican, en su gama más avanzada, casi exclusivamente en Taiwán. Esa concentración extrema convirtió a una isla de 36.000 km² en el punto más sensible de la economía mundial, y es una de las razones por las que la tensión entre China y Taiwán preocupa mucho más allá de la región: una interrupción ahí frenaría fábricas en todo el planeta.",
        },
        en: {
          role: "The most contested link",
          title: "Taiwan and the chips nobody else can make",
          body: "The battery powers a phone whose brain is a set of chips that, at the most advanced end, are made almost exclusively in Taiwan. That extreme concentration turned a 36,000 km² island into the single most sensitive point in the world economy, and it's one reason tension between China and Taiwan worries people far beyond the region: an interruption there would stall factories across the planet.",
        },
      },
    ],
  },
  {
    slug: "viaje-de-unos-jeans",
    slugEn: "journey-of-a-pair-of-jeans",
    emoji: "👖",
    related: [
      "revolucion-industrial",
      "migracion-diasporas",
      "capitalismo-y-alternativas",
    ],
    es: {
      title: "El viaje de unos jeans",
      subtitle: "Algodón, agua, trabajo y un mar que desapareció",
      intro:
        "Una prenda de menos de veinte dólares puede haber recorrido más kilómetros que la mayoría de las personas. El recorrido de unos jeans muestra cómo funciona realmente la industria más deslocalizada del mundo, y cuánto cuesta de verdad lo que pagamos poco.",
      closing:
        "La industria textil es el caso más nítido de una cadena global optimizada al centavo: cada etapa se ubica donde sea más barata, y el precio final rara vez incluye el costo ambiental ni el riesgo laboral. Cuando esos costos aparecen —un río contaminado, un edificio que se derrumba— los paga el país que produce, no el que consume.",
    },
    en: {
      title: "The journey of a pair of jeans",
      subtitle: "Cotton, water, labour and a sea that disappeared",
      intro:
        "A garment costing under twenty dollars may have travelled further than most people do. Following a pair of jeans shows how the world's most offshored industry actually works, and what the things we pay little for really cost.",
      closing:
        "The textile industry is the clearest case of a global chain optimised to the cent: each stage sits wherever it's cheapest, and the final price rarely includes the environmental cost or the workplace risk. When those costs do surface — a poisoned river, a building collapse — they're paid by the country that produces, not the one that consumes.",
    },
    stops: [
      {
        country: "Uzbekistan",
        emoji: "💧",
        es: {
          role: "El costo invisible",
          title: "Uzbekistán y el Mar de Aral",
          body: "El algodón necesita enormes cantidades de agua. En la época soviética se desviaron los dos ríos que alimentaban el Mar de Aral para regar plantaciones de algodón en Asia Central, y el que era el cuarto lago más grande del mundo se redujo a una fracción de su tamaño: barcos varados en un desierto de sal. Es uno de los desastres ambientales provocados más documentados de la historia, y empezó como una decisión agrícola.",
        },
        en: {
          role: "The invisible cost",
          title: "Uzbekistan and the Aral Sea",
          body: "Cotton needs enormous amounts of water. In the Soviet era the two rivers feeding the Aral Sea were diverted to irrigate cotton plantations across Central Asia, and what was the world's fourth-largest lake shrank to a fraction of its size: ships stranded in a salt desert. It's one of the most thoroughly documented man-made environmental disasters in history, and it began as an agricultural decision.",
        },
      },
      {
        country: "India",
        emoji: "🧵",
        es: {
          role: "Dónde se hila",
          title: "India, del telar artesanal a la industria",
          body: "India es uno de los mayores productores de algodón y de hilado del mundo, y eso tiene una ironía histórica: durante el dominio británico, la industria textil india fue deliberadamente desarticulada para que la colonia exportara algodón en bruto y comprara telas fabricadas en Inglaterra. El boicot a esas telas y la rueca hilando a mano fueron símbolos centrales del movimiento independentista de Gandhi.",
        },
        en: {
          role: "Where it's spun",
          title: "India, from the handloom to industry",
          body: "India is one of the world's largest producers of cotton and yarn, which carries a historical irony: under British rule, India's textile industry was deliberately dismantled so the colony would export raw cotton and buy cloth woven in England. Boycotting that cloth, and spinning by hand, became central symbols of Gandhi's independence movement.",
        },
      },
      {
        country: "Bangladesh",
        emoji: "🏗️",
        es: {
          role: "Dónde se cose",
          title: "Bangladés, la fábrica de ropa del mundo",
          body: "Bangladés es el segundo exportador mundial de indumentaria, y el sector emplea a millones de personas, en su mayoría mujeres. Esa industria sacó a buena parte del país de la pobreza extrema y al mismo tiempo puso en evidencia sus costos: el derrumbe del edificio Rana Plaza en 2013, con más de mil muertos, obligó a un replanteo internacional sobre la responsabilidad de las marcas respecto de las condiciones en las fábricas que contratan.",
        },
        en: {
          role: "Where it's sewn",
          title: "Bangladesh, the world's clothing factory",
          body: "Bangladesh is the world's second-largest garment exporter, and the sector employs millions of people, most of them women. That industry lifted much of the country out of extreme poverty while also exposing its costs: the 2013 collapse of the Rana Plaza building, killing more than a thousand people, forced an international reckoning over how responsible brands are for conditions in the factories they contract.",
        },
      },
      {
        country: "Ghana",
        emoji: "🗑️",
        es: {
          role: "Dónde terminan",
          title: "Ghana y el mercado de la ropa que sobra",
          body: "El viaje no termina en el placard. Una parte enorme de la ropa donada o descartada en países ricos se exporta a granel a África Occidental. En el mercado de Kantamanto, en Accra, se revende lo aprovechable, pero un porcentaje muy alto llega en condiciones invendibles y termina en vertederos y playas. En twi local a esa ropa se la llama \"obroni wawu\": la ropa del blanco muerto.",
        },
        en: {
          role: "Where they end up",
          title: "Ghana and the market for leftover clothes",
          body: "The journey doesn't end in a wardrobe. A huge share of the clothing donated or discarded in rich countries is exported in bulk to West Africa. At Accra's Kantamanto market, what's usable is resold, but a very high proportion arrives unsellable and ends up in landfills and on beaches. In local Twi that clothing is called \"obroni wawu\": dead white man's clothes.",
        },
      },
      {
        country: "Chile",
        emoji: "🏜️",
        es: {
          role: "Dónde se acumulan",
          title: "El desierto de Atacama como vertedero",
          body: "Al norte de Chile, la zona franca de Iquique importa ropa usada por decenas de miles de toneladas al año para revenderla a toda Sudamérica. Lo que no se vende no se puede reexportar ni ingresar legalmente al resto del país, así que se descarga en el desierto, cerca de Alto Hospicio. Los montones son visibles desde el espacio. En el lugar más seco del planeta casi nada se degrada, y la fibra sintética —que es plástico— tampoco: cuando los vertederos se queman, para hacer lugar o por accidente, el humo llega a las poblaciones vecinas.",
        },
        en: {
          role: "Where they pile up",
          title: "The Atacama desert as a dump",
          body: "In northern Chile, the Iquique free-trade zone imports tens of thousands of tonnes of used clothing a year to resell across South America. Whatever doesn't sell can neither be re-exported nor legally enter the rest of the country, so it is tipped into the desert near Alto Hospicio. The piles are visible from space. In the driest place on earth almost nothing degrades, and synthetic fibre — which is plastic — does not either: when the dumps burn, to make room or by accident, the smoke reaches the towns nearby.",
        },
      },
    ],
  },
  {
    slug: "viaje-del-chocolate",
    slugEn: "journey-of-chocolate",
    emoji: "🍫",
    related: [
      "esclavitud-comercio-triangular",
      "colonizacion-divisiones",
      "pbi-idh",
    ],
    es: {
      title: "El viaje del chocolate",
      subtitle: "Un cultivo americano, cosechado en África, transformado en Europa",
      intro:
        "El cacao es originario de América, se cultiva sobre todo en África y se convierte en chocolate principalmente en Europa. Ese triángulo, por sí solo, ya cuenta una historia de cinco siglos.",
      closing:
        "Cuando un producto vale mucho más después de cruzar el océano que antes de hacerlo, la pregunta que importa no es cuánto vale, sino dónde ocurre la transformación y quién es dueño de esa etapa. Esa pregunta —y no la geografía— explica la mayor parte de las diferencias de ingreso entre países.",
    },
    en: {
      title: "The journey of chocolate",
      subtitle: "An American crop, harvested in Africa, transformed in Europe",
      intro:
        "Cacao is native to the Americas, grown mostly in Africa, and turned into chocolate mainly in Europe. That triangle alone already tells a five-century story.",
      closing:
        "When a product is worth far more after crossing the ocean than before, the question that matters isn't what it's worth but where the transformation happens and who owns that stage. That question — not geography — explains most of the income gap between countries.",
    },
    stops: [
      {
        country: "Mexico",
        emoji: "🏺",
        es: {
          role: "El origen",
          title: "México, donde el cacao era moneda",
          body: "El cacao se cultivaba y consumía en Mesoamérica mucho antes de la llegada europea. Entre mexicas y mayas no solo era una bebida ritual —amarga, con especias, nada parecida al chocolate dulce actual— sino que los granos funcionaban literalmente como moneda de cambio. La palabra \"chocolate\" llega al español desde el náhuatl.",
        },
        en: {
          role: "The origin",
          title: "Mexico, where cacao was currency",
          body: "Cacao was grown and consumed in Mesoamerica long before Europeans arrived. Among the Mexica and Maya it wasn't only a ritual drink — bitter, spiced, nothing like today's sweet chocolate — the beans literally functioned as currency. The word \"chocolate\" reaches English through Spanish from Nahuatl.",
        },
      },
      {
        country: "Ivory Coast",
        emoji: "🌳",
        es: {
          role: "Dónde se cosecha hoy",
          title: "Costa de Marfil, casi la mitad del cacao mundial",
          body: "Costa de Marfil y Ghana producen juntas cerca de dos tercios del cacao del planeta, un cultivo que las potencias coloniales trasladaron desde América a África Occidental. La mayor parte sale de pequeñas explotaciones familiares, y el ingreso de esos productores depende de un precio internacional que ellos no controlan. El trabajo infantil en la cosecha es un problema persistente y documentado, que la industria se comprometió a erradicar en varias ocasiones sin lograrlo del todo.",
        },
        en: {
          role: "Where it's harvested today",
          title: "Côte d'Ivoire, almost half the world's cocoa",
          body: "Côte d'Ivoire and Ghana together produce close to two thirds of the planet's cocoa, a crop colonial powers moved from the Americas to West Africa. Most comes from small family farms, and those growers' income depends on an international price they don't control. Child labour in the harvest is a persistent, documented problem that the industry has repeatedly pledged to end without fully doing so.",
        },
      },
      {
        country: "Switzerland",
        emoji: "🍬",
        es: {
          role: "Dónde se vuelve caro",
          title: "Suiza, un país sin cacao famoso por su chocolate",
          body: "En Suiza no crece un solo árbol de cacao, y sin embargo \"chocolate suizo\" es una de las marcas país más valiosas del mundo. Fue ahí donde se inventaron el chocolate con leche y el conchado, la técnica que le da su textura. Ese salto técnico, sumado a un siglo de construcción de marca, hace que el valor se concentre en la fábrica europea y no en la plantación africana.",
        },
        en: {
          role: "Where it becomes expensive",
          title: "Switzerland, a country with no cacao famous for chocolate",
          body: "Not a single cacao tree grows in Switzerland, and yet \"Swiss chocolate\" is one of the world's most valuable country brands. It was there that milk chocolate and conching — the technique that gives chocolate its texture — were invented. That technical leap, plus a century of brand-building, concentrates the value in the European factory rather than the African plantation.",
        },
      },
      {
        country: "Ghana",
        emoji: "⚖️",
        es: {
          role: "El intento de cambiar el reparto",
          title: "Ghana y el precio que se negocia entre dos países",
          body: "En 2019 Ghana y Costa de Marfil hicieron algo inédito: acordaron negociar juntas y exigir un sobreprecio fijo por tonelada destinado a los productores, apodado \"prima de ingreso digno\". La lógica es la misma que la de la OPEP con el petróleo: si dos países controlan la mayor parte de la oferta mundial, pueden intentar fijar condiciones en lugar de aceptarlas. El resultado fue mixto, pero marcó un precedente.",
        },
        en: {
          role: "The attempt to change the split",
          title: "Ghana and a price negotiated between two countries",
          body: "In 2019 Ghana and Côte d'Ivoire did something unprecedented: they agreed to negotiate jointly and demand a fixed premium per tonne earmarked for growers, dubbed the \"living income differential\". The logic is the same as OPEC's with oil: if two countries control most of world supply, they can try to set terms rather than accept them. The results were mixed, but it set a precedent.",
        },
      },
    ],
  },
];

export const findLearnRoute = (slug?: string) =>
  learnRoutes.find((route) => route.slug === slug || route.slugEn === slug);

/**
 * One stop, with the thread it belongs to.
 *
 * A route is a story told across countries, but tapping a stop used to drop
 * the reader on a country page that says nothing about coffee — the thread
 * simply ended. A reader put it plainly: following the route, it doesn't pay
 * to tap another stop, because tapping takes you out of the subject. So the
 * country page needs to know which story brought you there, and where that
 * story goes next.
 */
export interface RouteThread {
  route: LearnRoute;
  stop: LearnRouteStop;
  /** 1-based, for "stop 2 of 5". */
  position: number;
  total: number;
  previous?: LearnRouteStop;
  next?: LearnRouteStop;
}

export const findRouteThread = (
  routeSlug: string | null | undefined,
  englishCountryName: string | undefined
): RouteThread | undefined => {
  if (!routeSlug || !englishCountryName) return undefined;
  const route = findLearnRoute(routeSlug);
  if (!route) return undefined;

  const index = route.stops.findIndex(
    (stop) => stop.country === englishCountryName
  );
  // A country that isn't on this route: the parameter is stale or hand-edited.
  if (index < 0) return undefined;

  return {
    route,
    stop: route.stops[index],
    position: index + 1,
    total: route.stops.length,
    previous: route.stops[index - 1],
    next: route.stops[index + 1],
  };
};
